import { useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BillType } from '@features/bills/enums/billType'
import { SubjectReconciliationSuggestionCard } from '@features/paymentReconciliationModal/elements/SubjectReconciliationSuggestionCard'
import {
  useDeleteSubjectReconciliationSuggestion,
  useSubjectReconciliationSuggestions,
} from '@features/paymentReconciliationModal/hooks'
import { reconciliationSuggestHasDifference } from '@features/paymentReconciliationModal/utils'
import { notifyPaymentRegistrationError } from '@features/paymentReconciliationModal/utils/notifyPaymentRegistrationError'
import {
  BankMatchRejectionReason,
  BankMatchRejectionReasonValue,
  BankMatchUiFlow,
  UiSource,
} from '@features/reconciliation/enums'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../config/queryClient'
import { ModalId } from '../../../../enums/modalId'
import { QueryKeys } from '../../../../enums/queryKeys'
import { Scope } from '../../../../enums/scope'
import { useSegment } from '../../../../hooks'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { useReconcileBill } from '../../hooks/useReconcileBill'
import { useBillView } from '../BillReadableForm'

export const BillOverviewReconciliationSuggestionContent = (): ReactElement | null => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { billId, billData } = useBillView()
  const { suggestions } = useSubjectReconciliationSuggestions(billData?.bill)
  const reconciliationTransaction = suggestions?.[0]?.transactions?.[0]
  const isCrossCurrency = useMemo(
    () => reconciliationTransaction?.currency !== billData?.bill?.currencyId,
    [reconciliationTransaction, billData?.bill?.currencyId],
  )
  const hasDifference = reconciliationSuggestHasDifference(reconciliationTransaction, billData?.bill)

  const { track } = useSegment()

  const { deleteSuggestion, isProcessing: isProcessingDelete } = useDeleteSubjectReconciliationSuggestion({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.Bill, billId])
      queryClient.invalidateQueries([QueryKeys.BillReconciliationMatches, billId])
    },
    onError: () => {
      notifyPaymentRegistrationError(t)
    },
  })
  const { open: openBillReconciliationModal } = useModal(ModalId.BillReconciliationModal, {
    onOpen: () => {
      track('xxx - expenses - Pay and reconcile modal opened')
    },
  })

  const handleDeleteSuggestion = useCallback(
    (rejectionReason?: BankMatchRejectionReasonValue) => {
      if (!organization?.id || !suggestions?.length) {
        return
      }
      deleteSuggestion({
        organizationId: organization?.id,
        suggestion: suggestions?.[0],
        uiSource: UiSource.PurchaseScreen,
        rejectionReason,
      })
    },
    [organization?.id, suggestions, deleteSuggestion],
  )

  const onSuccessReconciliation = useCallback(() => {
    /* eslint-disable @typescript-eslint/naming-convention */
    track('Bill Payment Created (Client)', {
      bill_id: billId,
      is_auto_matched: true,
      payment_mode: 'pay and reconcile',
    })
    track('XXX - Reconciliation - Reconciliation Suggestion Accepted (Client)')

    /** After a successful reconciliation, we remove the suggestion from the database.
     * This prevents the same suggestion from being shown again if the payment is voided in the future.
     */
    handleDeleteSuggestion(BankMatchRejectionReason.SuggestionAccepted)
  }, [track, billId, handleDeleteSuggestion])

  const subjectAmount = billData?.bill?.balance
  const crossCurrencyPaymentAmount = isCrossCurrency ? subjectAmount : undefined
  const { reconcileSubject: reconcileBill, isProcessing: isProcessingReconcile } = useReconcileBill({
    billId,
    organizationId: organization?.id,
    bankLineId: reconciliationTransaction?.id,
    crossCurrencyPaymentAmount,
    isAutoMatch: true,
    onSuccess: onSuccessReconciliation,
    uiSource: UiSource.PurchaseScreen,
    uiFlow: BankMatchUiFlow.Matched,
  })
  const isProcessing = isProcessingDelete || isProcessingReconcile

  const handleReject = useCallback(() => {
    if (!organization?.id || !suggestions?.length) {
      return
    }

    track('XXX - Reconciliation - Reconciliation Suggestion Rejected (Client)')
    handleDeleteSuggestion()
  }, [suggestions, handleDeleteSuggestion, organization?.id, track])

  const handleAccept = useCallback(() => {
    if (hasDifference) {
      openBillReconciliationModal()
      return
    }

    reconcileBill()
  }, [hasDifference, reconcileBill, openBillReconciliationModal])

  if (!isAuthorized(Scope.BankReconciliationAutoMatch) || !suggestions?.length) {
    return null
  }

  return (
    <SubjectReconciliationSuggestionCard
      handleAcceptSuggestion={handleAccept}
      handleRejectSuggestion={handleReject}
      isProcessingSuggestion={isProcessing}
      isProcessingDelete={isProcessingDelete}
      isProcessingReconcile={isProcessingReconcile}
      subjectType={BillType.Bill}
      suggestions={suggestions}
    />
  )
}
